export function initSecurityMonitoring() {
    if (typeof window === 'undefined') return

    // Define suspicious parameter checker function first
    const isSuspiciousParameter = (key, value) => {
        // Suspicious parameter patterns based on the CSP violations
        const suspiciousPatterns = [
            // Multiple URL encoding patterns
            /%25253D/i, // Triple URL encoding of equals
            /%2525253D/i, // Quintuple URL encoding
            /%253D%25253D/i, // Double triple encoding

            // Double encoded equals patterns
            /=%3D%3D/i, // Equals followed by double encoded equals
            /%3D%25253D/i, // Mixed encoding patterns

            // Suspicious value patterns from CSP violations
            /=[A-Za-z0-9]{5}$/i, // VA5t5, Kl97k, etc.
            /=%27%5B0%5D/i, // Encoded ['[0]']
            /=VA5t5/i, // Specific pattern from violations

            // utm parameter with encoded values
            /-1%253D=/i, // -1 with encoded equals
            /%2527%2527/i, // Double encoded single quotes

            // Base64-like patterns with multiple encoding
            /ZGVtaXpBQnY/i, // Specific base64 pattern from examples

            // Nested utm parameter patterns
            /utm_\w+.*%25253D/i, // Any utm parameter with triple encoding
        ]

        // Check for suspicious UTM parameter names with encoding
        const suspiciousUtmKeys = [
            /^utm_device%/i,
            /^utm_medium%/i,
            /^utm_referrer%/i,
            /^utm_id%/i,
            // Also check for the exact patterns from violations
            /utm_device.*%253D.*%252527.*%252527/i,
        ]

        // Check for suspicious keys
        if (suspiciousUtmKeys.some((pattern) => key.match(pattern))) {
            return true
        }

        // Check for suspicious patterns in values
        return suspiciousPatterns.some((pattern) => value.match(pattern))
    }

    // Wait for Sentry to be available
    const waitForSentry = () => {
        return new Promise((resolve) => {
            if (window.Sentry) {
                resolve(window.Sentry)
                return
            }

            let observer = null
            let timeoutId = null
            let resolved = false

            const cleanup = (result) => {
                if (resolved) return
                resolved = true

                if (observer) {
                    observer.disconnect()
                    observer = null
                }
                if (timeoutId) {
                    clearTimeout(timeoutId)
                    timeoutId = null
                }
                resolve(result)
            }

            // Watch for script tag additions that might load Sentry
            observer = new MutationObserver(() => {
                if (window.Sentry) {
                    cleanup(window.Sentry)
                }
            })

            observer.observe(document.head, { childList: true, subtree: true })

            // Timeout after 5 seconds as fallback
            timeoutId = setTimeout(() => {
                cleanup(null)
            }, 5000)
        })
    }

    const monitorUrlChange = async (url) => {
        const urlObj = new URL(url, window.location.origin)
        let foundSuspicious = false

        // Check for suspicious URL parameter patterns
        const suspiciousParams = []
        urlObj.searchParams.forEach((value, key) => {
            if (isSuspiciousParameter(key, value)) {
                foundSuspicious = true
                suspiciousParams.push({ key, value })
                // Remove the suspicious parameter
                urlObj.searchParams.delete(key)
            }
        })

        if (foundSuspicious) {
            // Wait for Sentry and then report
            const sentry = await waitForSentry()
            if (sentry) {
                sentry.captureMessage('Malicious URL Parameter Detected', {
                    level: 'error',
                    tags: { security: 'url_hijack' },
                    extra: {
                        suspiciousParams,
                        originalUrl: url,
                        currentUrl: window.location.href,
                        detectionType: 'pattern_match',
                        userAgent: navigator.userAgent,
                    },
                })
            } else {
                console.warn('Sentry not available for security monitoring')
            }

            // Redirect to clean URL
            if (urlObj.href !== window.location.href) {
                window.location.replace(urlObj.href)
            }
        }
    }

    // Rest of your existing code remains the same...
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState

    const checkCurrentUrl = () => {
        monitorUrlChange(window.location.href)
    }

    history.pushState = function (state, title, url) {
        if (url) monitorUrlChange(url)
        return originalPushState.apply(this, arguments)
    }

    history.replaceState = function (state, title, url) {
        if (url) monitorUrlChange(url)
        return originalReplaceState.apply(this, arguments)
    }

    window.addEventListener('popstate', checkCurrentUrl)

    // Initial check with delay to ensure Sentry is loaded
    setTimeout(checkCurrentUrl, 1000)

    // Also run the check immediately
    checkCurrentUrl()
}
